import { useState, useEffect } from "react";
import AdminLayout from "../../components/AdminLayout";
import { getMonitoredSites, aiGenerateArticle } from "../../lib/api";
import { toast } from "sonner";
import { 
  Globe, 
  Sparkles, 
  RefreshCw,
  CheckCircle,
  ExternalLink,
  Zap,
  Rocket
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AIMonitor() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [runningAll, setRunningAll] = useState(false);
  const [lastRunResult, setLastRunResult] = useState(null);

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await getMonitoredSites();
        setSites(response.data);
      } catch (error) {
        console.error("Error fetching sites:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSites();
  }, []);

  const handleGenerate = async (site) => {
    setGenerating(site.id);
    try {
      // Simulate AI rewriting a sample article
      await aiGenerateArticle({
        original_title: `Novidades do Arrocha em ${site.name}`,
        original_content: `Confira as últimas notícias sobre o mundo do Arrocha diretamente de ${site.name}. O ritmo que conquistou a Bahia e o Brasil continua fazendo sucesso com novos lançamentos e shows lotados.`,
        source_url: site.url,
        source_name: site.name,
      });
      toast.success(`Matéria gerada a partir de ${site.name}!`);
    } catch (error) {
      console.error("Generate error", error);
      toast.error("Erro ao gerar matéria com IA");
    } finally {
      setGenerating(null);
    }
  };

  const handleRunAll = async () => {
    if (runningAll) return;
    if (!window.confirm("Vai escanear TODOS os sites monitorados, reescrever as matérias e salvar como rascunho. Pode levar alguns minutos. Continuar?")) {
      return;
    }
    setRunningAll(true);
    setLastRunResult(null);
    const toastId = toast.loading("Escaneando todos os sites e gerando matérias... isso pode levar alguns minutos.");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/api/automation/run`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const details = data.details || data.results || data || {};
        const saved = details.saved_as_draft ?? 0;
        const scraped = details.scraped ?? 0;
        setLastRunResult({ scraped, saved });
        toast.success(`✅ ${saved} novas matérias salvas como rascunho! Veja em "Automação".`, { id: toastId, duration: 8000 });
      } else {
        toast.error(data.detail || "Erro ao executar automação", { id: toastId });
      }
    } catch (err) {
      console.error("Run all error", err);
      toast.error("Erro de conexão", { id: toastId });
    } finally {
      setRunningAll(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-[#00E5FF]/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#00E5FF]" />
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white" data-testid="ai-monitor-title">
                Monitor de IA
              </h1>
            </div>
            <p className="text-zinc-500">
              Sites monitorados pela IA para reescrita automática de matérias
            </p>
          </div>

          {/* MASS GENERATE BUTTON */}
          <button
            onClick={handleRunAll}
            disabled={runningAll}
            className="group relative inline-flex items-center gap-2.5 px-5 py-3 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00E5FF] text-white font-bold text-sm shadow-lg shadow-[#FF007F]/25 hover:shadow-[#FF007F]/40 transition-all disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
            data-testid="generate-all-btn"
          >
            {runningAll ? (
              <>
                <RefreshCw size={18} className="animate-spin" />
                Gerando matérias...
              </>
            ) : (
              <>
                <Rocket size={18} />
                Gerar Matérias de Todos
              </>
            )}
          </button>
        </div>

        {/* Last run summary */}
        {lastRunResult && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-4 flex items-center gap-3" data-testid="last-run-result">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
            <p className="text-sm text-zinc-300">
              <span className="font-bold text-green-400">{lastRunResult.saved} novas matérias</span> salvas como rascunho de <span className="text-white">{lastRunResult.scraped}</span> notícias escaneadas. Veja em <a href="/admin/automation" className="text-[#00E5FF] hover:underline">Automação</a>.
            </p>
          </div>
        )}

        {/* Info Card */}
        <div className="bg-gradient-to-r from-[#00E5FF]/10 to-[#FF007F]/10 rounded-2xl p-6 border border-white/10">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#00E5FF]/20 flex items-center justify-center flex-shrink-0">
              <Zap className="w-6 h-6 text-[#00E5FF]" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold text-white mb-2">
                Como funciona a IA
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed mb-3">
                O sistema monitora automaticamente os principais portais de Arrocha listados abaixo. 
                Quando uma notícia relevante é identificada, a IA Claude Sonnet 4.5 reescreve o conteúdo 
                de forma original, mantendo as informações importantes mas com um texto totalmente novo.
                Os créditos da fonte original são sempre incluídos no final de cada matéria.
              </p>
              <div className="flex items-start gap-2 text-sm bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-zinc-300">
                  <span className="font-bold text-amber-400">Nada é publicado automaticamente.</span> Toda matéria gerada vai pra fila de <a href="/admin/automation" className="text-[#00E5FF] hover:underline font-semibold">Aprovação</a> como rascunho. Você revisa, edita se quiser e aprova só o que for pertinente. As fotos vêm primeiro do banco de fotos do artista; se não houver, usa a foto original da fonte.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sites Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-[#00E5FF] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sites.map((site, index) => (
              <div
                key={site.id}
                className="bg-[#121212] rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all animate-fade-in-up opacity-0"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`site-${site.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-zinc-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">{site.name}</h3>
                      <a
                        href={site.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-zinc-500 hover:text-[#FF007F] flex items-center gap-1 transition-colors"
                      >
                        {site.url.replace(/https?:\/\//, '').slice(0, 30)}
                        <ExternalLink size={12} />
                      </a>
                    </div>
                  </div>
                  <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    site.is_active 
                      ? "bg-green-500/20 text-green-500" 
                      : "bg-zinc-500/20 text-zinc-500"
                  }`}>
                    <CheckCircle size={12} />
                    {site.is_active ? "Ativo" : "Inativo"}
                  </span>
                </div>

                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
                  <span className="text-xs text-zinc-600">
                    ID: {site.id}
                  </span>
                  <button
                    onClick={() => handleGenerate(site)}
                    disabled={generating === site.id}
                    className="flex items-center gap-2 px-4 py-2 bg-[#00E5FF]/10 hover:bg-[#00E5FF]/20 text-[#00E5FF] rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                    data-testid={`generate-${site.id}`}
                  >
                    {generating === site.id ? (
                      <>
                        <RefreshCw size={14} className="animate-spin" />
                        Gerando...
                      </>
                    ) : (
                      <>
                        <Sparkles size={14} />
                        Gerar Matéria
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Legend */}
        <div className="bg-[#121212] rounded-2xl p-6 border border-white/10">
          <h3 className="font-heading text-lg font-bold text-white mb-4">
            Legenda de Status
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-500">
                <CheckCircle size={12} />
                Ativo
              </span>
              <span className="text-sm text-zinc-500">Site sendo monitorado</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ai-badge">
                <Sparkles size={12} />
                IA
              </span>
              <span className="text-sm text-zinc-500">Conteúdo da Equipe Arrocha Play</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-[#FF007F]/20 text-[#FF007F]">
                <RefreshCw size={12} />
                Manual
              </span>
              <span className="text-sm text-zinc-500">Gerar matéria manualmente</span>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
